import { show, hide, toggle, isHidden } from 'helpers/dom-utils'
import type {
  InstantSearchResultsUpdatedDetail,
  InstantSearchCollectionsUpdatedDetail
} from './instant-search.types'
import { SearchStateManager } from './search-state-manager'
import { updateViewResultsButton, shouldShowViewResultsButton } from 'helpers/view-results-button'

export class SearchModalElement extends HTMLElement {
  private static readonly CONFIG = {
    MIN_SEARCH_LENGTH: 3,
    DEBOUNCE_DELAY: 300,
    CAROUSEL_RESIZE_DELAY: 100
  }

  private searchInput: HTMLInputElement | null = null
  private resultsContainer: HTMLElement | null = null
  private resultsComponent: HTMLElement | null = null
  private collectionsContainer: HTMLElement | null = null
  private collectionsComponent: HTMLElement | null = null
  private defaultContent: HTMLElement | null = null
  private popularSearches: HTMLElement | null = null
  private viewResultsButtons: NodeListOf<HTMLElement> | null = null
  private viewResultsWrapper: HTMLElement | null = null
  private noResultsContainer: HTMLElement | null = null
  private searchTermSpan: HTMLElement | null = null
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private modalElement: HTMLElement | null = null
  private modalObserver: MutationObserver | null = null

  private stateManager = new SearchStateManager()

  private boundHandlers = {
    input: this.handleInput.bind(this),
    clear: this.handleClear.bind(this),
    resultsUpdate: this.handleResultsUpdate.bind(this),
    collectionsUpdate: this.handleCollectionsUpdate.bind(this)
  }

  connectedCallback() {
    this.initializeElements()
    this.addEventListeners()
    this.setupModalObserver()
  }

  disconnectedCallback() {
    this.removeEventListeners()
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
    if (this.modalObserver) {
      this.modalObserver.disconnect()
    }
  }

  private initializeElements() {
    this.searchInput = this.querySelector('input[name="q"]')
    this.resultsContainer = this.querySelector('[data-instant-results]')
    this.resultsComponent = this.querySelector('x-instant-search-results')
    this.collectionsContainer = this.querySelector('[data-instant-collections]')
    this.collectionsComponent = this.querySelector('x-instant-search-collections')
    this.defaultContent = this.querySelector('[data-default-content]')
    this.popularSearches = this.querySelector('[data-popular-searches]')
    this.viewResultsButtons = this.querySelectorAll('[data-view-results]')
    this.viewResultsWrapper = this.querySelector('[data-view-results-wrapper]')
    this.noResultsContainer = this.querySelector('[data-no-results]')
    this.searchTermSpan = this.querySelector('[data-search-term]')
    this.modalElement = this.querySelector('x-modal')
  }

  private addEventListeners() {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.boundHandlers.input)
    }

    const clearButton = this.querySelector('[data-button-clear]')
    if (clearButton) {
      clearButton.addEventListener('click', this.boundHandlers.clear)
    }

    window.addEventListener('instant-search:results-updated', this.boundHandlers.resultsUpdate)
    window.addEventListener('instant-search:collections-updated', this.boundHandlers.collectionsUpdate)
  }

  private removeEventListeners() {
    if (this.searchInput) {
      this.searchInput.removeEventListener('input', this.boundHandlers.input)
    }

    const clearButton = this.querySelector('[data-button-clear]')
    if (clearButton) {
      clearButton.removeEventListener('click', this.boundHandlers.clear)
    }

    window.removeEventListener('instant-search:results-updated', this.boundHandlers.resultsUpdate)
    window.removeEventListener('instant-search:collections-updated', this.boundHandlers.collectionsUpdate)
  }

  private handleInput(event: Event) {
    const target = event.target as HTMLInputElement
    const searchTerm = target.value.trim()

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      this.updateResults(searchTerm)
    }, SearchModalElement.CONFIG.DEBOUNCE_DELAY)
  }

  private handleClear() {
    this.stateManager.reset()
    this.updateResults('')
  }

  private updateResults(searchTerm: string) {
    if (searchTerm.length >= SearchModalElement.CONFIG.MIN_SEARCH_LENGTH) {
      this.showSearchResults(searchTerm)
    } else {
      this.resetToDefault()
    }
  }

  private showSearchResults(searchTerm: string) {
    if (!this.resultsComponent || !this.resultsContainer) return

    this.resultsComponent.setAttribute('search-term', searchTerm)
    show(this.resultsContainer)

    if (this.collectionsComponent) {
      this.collectionsComponent.setAttribute('search-term', searchTerm)
    }
    show(this.collectionsContainer, true)

    if (this.searchTermSpan) {
      this.searchTermSpan.textContent = `"${searchTerm}"`
    }
  }

  private resetToDefault() {
    if (!this.resultsComponent || !this.resultsContainer) return

    this.resultsComponent.setAttribute('search-term', '')
    hide(this.resultsContainer)

    if (this.collectionsComponent) {
      this.collectionsComponent.setAttribute('search-term', '')
    }
    hide(this.collectionsContainer)
    show(this.defaultContent)
    show(this.popularSearches)
    hide(this.noResultsContainer)
    this.updateViewResultsButtons(0)
  }

  private handleResultsUpdate(event: Event) {
    const customEvent = event as CustomEvent<InstantSearchResultsUpdatedDetail>
    const { count = 0, searchTerm = '', isLoading = false } = customEvent.detail || {}

    this.stateManager.updateProducts(count, isLoading)
    this.stateManager.updateSearchTerm(searchTerm)

    this.updateViewResultsButtons(count, searchTerm)
    this.updateNoResultsVisibility(searchTerm)
    this.updateDefaultContentVisibility(searchTerm)
    this.updateResultsContainerVisibility()
  }

  private handleCollectionsUpdate(event: Event) {
    const customEvent = event as CustomEvent<InstantSearchCollectionsUpdatedDetail>
    const { count = 0, searchTerm = '', isLoading = false } = customEvent.detail || {}

    this.stateManager.updateCollections(count, isLoading)
    this.stateManager.updateSearchTerm(searchTerm)

    toggle(this.collectionsContainer, count > 0, true)
    toggle(this.popularSearches, count === 0)

    this.updateNoResultsVisibility(searchTerm)
  }

  private updateViewResultsButtons(count: number, searchTerm: string = '') {
    if (this.viewResultsButtons) {
      this.viewResultsButtons.forEach(button => {
        updateViewResultsButton(button, { count, searchTerm }, show, hide)
      })
    }

    toggle(this.viewResultsWrapper, shouldShowViewResultsButton(count))
  }

  private updateNoResultsVisibility(searchTerm: string) {
    if (!this.noResultsContainer) return

    if (!searchTerm || searchTerm.length < SearchModalElement.CONFIG.MIN_SEARCH_LENGTH) {
      hide(this.noResultsContainer)
      return
    }

    if (this.stateManager.isAnyLoading) {
      hide(this.noResultsContainer)
      return
    }

    toggle(this.noResultsContainer, this.stateManager.hasNoResults)
  }

  private updateDefaultContentVisibility(searchTerm: string) {
    if (!this.defaultContent) return

    const wasHidden = isHidden(this.defaultContent)

    if (searchTerm && searchTerm.length >= SearchModalElement.CONFIG.MIN_SEARCH_LENGTH && !this.stateManager.hasProducts) {
      show(this.defaultContent)

      if (wasHidden) {
        this.resizeCarousels(this.defaultContent)
      }
    } else if (searchTerm && searchTerm.length >= SearchModalElement.CONFIG.MIN_SEARCH_LENGTH && this.stateManager.hasProducts) {
      hide(this.defaultContent)
    }
  }

  private resizeCarousels(container: HTMLElement, delay = SearchModalElement.CONFIG.CAROUSEL_RESIZE_DELAY) {
    setTimeout(() => {
      const carousels = container.querySelectorAll('x-carousel')
      carousels.forEach(carousel => {
        const flickityInstance = (carousel as any).flickity
        if (flickityInstance) {
          flickityInstance.resize()
        }
      })
    }, delay)
  }

  private updateResultsContainerVisibility() {
    if (!this.resultsContainer) return
    if (!this.stateManager.productsLoading && !this.stateManager.hasProducts) {
      hide(this.resultsContainer)
    } else if (this.stateManager.hasProducts) {
      show(this.resultsContainer)
    }
  }

  private setupModalObserver() {
    if (!this.modalElement) return

    this.modalObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ui') {
          const isVisible = this.modalElement?.getAttribute('data-ui') === 'visible'
          if (isVisible && this.searchInput) {
            setTimeout(() => {
              this.searchInput?.focus()
            }, 0)
          }
        }
      })
    })

    this.modalObserver.observe(this.modalElement, {
      attributes: true,
      attributeFilter: ['data-ui']
    })
  }
}
